/**
 * Custom React Flow edge for agent-set links.
 *  - Transfer edges: stock smooth-step with the function name on a chip near the
 *    target (one-way, single arrowhead).
 *  - Subagent "uses" edges: an explicit waypoint path computed by the layout
 *    (see AgentSetGraph.routeSubagentEdges) that routes the edge around nodes and
 *    into a per-tool vertical "bus"; double arrowheads (ask → result); the bus is
 *    labelled once (data.busLabel) rather than once per incoming edge.
 */
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath } from 'reactflow';

export const EDGE_COLOUR = {
  transfer: 'var(--joy-palette-primary-500, #0b6bcb)',
  subagent: 'var(--joy-palette-warning-600, #9a5b13)',
};

// Fraction along a transfer edge at which to anchor its label (toward target).
const LABEL_T = 0.62;

/** SVG path through waypoints with small rounded corners. */
function roundedPath(points, radius = 9) {
  if (!points || points.length < 2) return '';
  if (points.length === 2) {
    return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
  }
  let d = `M ${points[0].x},${points[0].y}`;
  for (let i = 1; i < points.length - 1; i += 1) {
    const p = points[i];
    const a = points[i - 1];
    const b = points[i + 1];
    const da = Math.hypot(p.x - a.x, p.y - a.y) || 1;
    const db = Math.hypot(b.x - p.x, b.y - p.y) || 1;
    const r = Math.min(radius, da / 2, db / 2);
    d += ` L ${p.x - ((p.x - a.x) / da) * r},${p.y - ((p.y - a.y) / da) * r}`;
    d += ` Q ${p.x},${p.y} ${p.x + ((b.x - p.x) / db) * r},${p.y + ((b.y - p.y) / db) * r}`;
  }
  const last = points[points.length - 1];
  d += ` L ${last.x},${last.y}`;
  return d;
}

function Chip({ x, y, colour, children }) {
  return (
    <EdgeLabelRenderer>
      <div
        className="nodrag nopan"
        style={{
          position: 'absolute',
          transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
          pointerEvents: 'none',
          fontSize: 10,
          fontFamily: 'ui-monospace, monospace',
          padding: '1px 6px',
          borderRadius: 6,
          whiteSpace: 'nowrap',
          color: colour,
          background: 'var(--joy-palette-background-surface, #fff)',
          border: `1px solid ${colour}`,
        }}
      >
        {children}
      </div>
    </EdgeLabelRenderer>
  );
}

export default function AgentSetEdge({
  id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition,
  data, markerEnd, markerStart, style,
}) {
  const colour = EDGE_COLOUR[data?.kind] || EDGE_COLOUR.transfer;

  // Subagent "uses" edge: explicit bus waypoints from the layout.
  if (data?.points?.length >= 2) {
    return (
      <>
        <BaseEdge id={id} path={roundedPath(data.points)} style={style}
          markerEnd={markerEnd} markerStart={markerStart} />
        {data.routeLabel && data.routeLabelAt && (
          <Chip x={data.routeLabelAt.x} y={data.routeLabelAt.y} colour={colour}>{data.routeLabel}</Chip>
        )}
      </>
    );
  }

  // Transfer edge: stock smooth-step with a function-name chip near the target.
  const [path] = getSmoothStepPath({
    sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, borderRadius: 14,
  });
  const lx = sourceX + (targetX - sourceX) * LABEL_T;
  const ly = sourceY + (targetY - sourceY) * LABEL_T;
  return (
    <>
      <BaseEdge id={id} path={path} style={style} markerEnd={markerEnd} markerStart={markerStart} />
      {data?.functionName && <Chip x={lx} y={ly} colour={colour}>{data.functionName}</Chip>}
    </>
  );
}
